'use client';

import { Popover, PopoverContent, PopoverTrigger } from '@zealot/ui/components/popover';
import type * as React from 'react';

import { ColorPicker, type ColorPickerProps } from './color-picker';
import { ColorPickerArea } from './color-picker-area';
import { ColorPickerInput } from './color-picker-input';
import { ColorPickerSwatch } from './color-picker-swatch';

type SimpleColorPickerProps = Pick<ColorPickerProps, 'value' | 'onChange' | 'format' | 'fallbackValue' | 'name'> & {
  id?: string;
  disabled?: boolean;
  ref?: React.Ref<HTMLButtonElement>;
  onBlur?: () => void;
  'aria-invalid'?: React.AriaAttributes['aria-invalid'];
  'aria-describedby'?: string;
  trigger?: React.ReactElement;
  triggerLabel?: string;
  areaClassName?: string;
};

const SimpleColorPicker = ({
  value,
  onChange,
  format,
  fallbackValue,
  name,
  id,
  disabled,
  ref,
  onBlur,
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedBy,
  trigger,
  triggerLabel,
  areaClassName,
}: SimpleColorPickerProps) => {
  const triggerProps = {
    id,
    ref,
    disabled,
    'aria-label': triggerLabel ?? (id ? undefined : 'Choose color'),
    'aria-invalid': ariaInvalid,
    'aria-describedby': ariaDescribedBy,
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) onBlur?.();
  };

  return (
    <ColorPicker
      value={value}
      onChange={onChange}
      format={format}
      fallbackValue={fallbackValue}
      disabled={disabled}
      className="contents"
    >
      <Popover onOpenChange={handleOpenChange}>
        {trigger ? (
          <PopoverTrigger {...triggerProps} nativeButton={false} render={trigger} />
        ) : (
          <PopoverTrigger
            {...triggerProps}
            className="inline-flex w-fit justify-self-start rounded-md outline-none focus-visible:ring-2 focus-visible:ring-ring/30 disabled:pointer-events-none disabled:opacity-50 aria-invalid:ring-2 aria-invalid:ring-destructive/40"
          >
            <ColorPickerSwatch className="size-8" />
          </PopoverTrigger>
        )}
        <PopoverContent data-slot="simple-color-picker-content" className="w-auto">
          <ColorPickerArea className={areaClassName} />
          <ColorPickerInput size="sm" />
        </PopoverContent>
      </Popover>
      {name && <input type="hidden" name={name} value={value ?? ''} disabled={disabled} />}
    </ColorPicker>
  );
};

export type { SimpleColorPickerProps };
export { SimpleColorPicker };
